import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Users, FolderKanban,
  X, User, LogOut, ChevronRight, KeyRound
} from 'lucide-react';
import BASE_URL from '../services/api';

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [userName, setUserName] = useState('');
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [hasPortals, setHasPortals] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);

  useEffect(() => {
    setUserName(localStorage.getItem('userName') || 'User');
    const stored = localStorage.getItem('portals');
    const portalList = stored ? JSON.parse(stored) : [];
    setHasPortals(portalList.length > 0);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setIsSuperAdmin(user.is_superadmin === true);
  }, []);

  const handleLogout = async () => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      await fetch(`${BASE_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ user_id: user.id }),
      });
    } catch (err) {
      // still logout even if API fails
    } finally {
      localStorage.removeItem('user');
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('token');
      localStorage.removeItem('userName');
      localStorage.removeItem('username');
      localStorage.removeItem('userRole');
      localStorage.removeItem('portals');
      navigate('/');
    }
  };

  const getMenuItems = () => {
    const items = [{ name: 'My Apps', path: '/my-apps', icon: FolderKanban }];
    if (isSuperAdmin) {
      items.push({ name: 'User Management', path: '/user-management', icon: Users });
    }
    return items;
  };

  const menuItems = getMenuItems();
  const isActive = (path) => location.pathname === path;
  const isSubmenuActive = (submenu) => submenu?.some(item => location.pathname === item.path);
  const toggleSubmenu = (itemName) => setOpenSubmenu(openSubmenu === itemName ? null : itemName);

  return (
    <>
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50 w-72 bg-white border-r border-gray-200 
        transform transition-transform duration-300 ease-in-out
        ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="h-16 flex items-center justify-between px-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <img src="Images/tn-logo.png" alt="TN Logo" className="w-10 h-10 object-contain" />
              <div>
                <h1 className="text-lg font-bold text-gray-800">ADTW</h1>
                <p className="text-xs text-gray-500">Management Portal</p>
              </div>
            </div>
            <button onClick={() => setIsSidebarOpen(false)} className="lg:hidden text-gray-500 hover:text-gray-700">
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-6 px-4">
            <div className="space-y-2">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const hasSubmenu = item.submenu && item.submenu.length > 0;
                const submenuOpen = openSubmenu === item.name;
                const submenuActive = isSubmenuActive(item.submenu);

                return (
                  <div key={item.name}>
                    {hasSubmenu ? (
                      <button
                        onClick={() => toggleSubmenu(item.name)}
                        className={`
                          w-full flex items-center justify-between gap-3 px-4 py-3 transition-all duration-200
                          ${submenuActive || submenuOpen ? 'bg-[#3d82c4] text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          <span className="font-medium text-sm">{item.name}</span>
                        </div>
                        <ChevronRight className={`w-4 h-4 transition-transform duration-200 ${submenuOpen ? 'rotate-90' : ''}`} />
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        onClick={() => setIsSidebarOpen(false)}
                        className={`
                          flex items-center justify-between gap-3 px-4 py-3 transition-all duration-200
                          ${active ? 'bg-[#3d82c4] text-white shadow-md' : 'text-gray-700 hover:bg-gray-100'}
                        `}
                      >
                        <div className="flex items-center gap-3">
                          <Icon className="w-5 h-5 flex-shrink-0" />
                          <span className="font-medium text-sm">{item.name}</span>
                        </div>
                        {active && <ChevronRight className="w-4 h-4" />}
                      </Link>
                    )}

                    {hasSubmenu && submenuOpen && (
                      <div className="ml-4 mt-1 space-y-1">
                        {item.submenu.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const subActive = isActive(subItem.path);
                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              onClick={() => setIsSidebarOpen(false)}
                              className={`
                                flex items-center gap-3 px-4 py-2.5 transition-all duration-200 text-sm
                                ${subActive ? 'bg-[#2d6fad] text-white' : 'text-gray-600 hover:bg-gray-100'}
                              `}
                            >
                              <SubIcon className="w-4 h-4 flex-shrink-0" />
                              <span className="font-medium">{subItem.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </nav>

          {/* User Profile & Logout */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-3 px-3 py-2 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-[#3d82c4] to-[#2d6fad] flex items-center justify-center">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{userName}</p>
                <p className="text-xs text-[#3d82c4] truncate font-medium">
                  {isSuperAdmin ? 'Super Admin' : 'Portal User'}
                </p>
              </div>
            </div>

            {/* Change Password - only for portal users */}
            {hasPortals && (
              <Link
                to="/change-password"
                onClick={() => setIsSidebarOpen(false)}
                className={`
                  w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-2
                  transition-colors font-medium text-sm
                  ${isActive('/change-password') ? 'bg-[#3d82c4] text-white' : 'bg-blue-50 text-[#3d82c4] hover:bg-blue-100'}
                `}
              >
                <KeyRound className="w-4 h-4" />
                <span>Change Password</span>
              </Link>
            )}

            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 hover:bg-red-100 transition-colors font-medium text-sm"
            >
              <LogOut className="w-4 h-4" />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsSidebarOpen(false)} />
      )}
    </>
  );
};

export default Sidebar;